import React from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import StickyNavigation from '@/components/layout/StickyNavigation';
import { useTenant } from '@/contexts/TenantContext';

const BusinessInsuranceGuide: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  // Tenant-aware prefix for navigation
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;

  return (
    <div className="min-h-screen bg-white">
      <StickyNavigation />
      
      {/* Hero Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Best Business Insurance in the UAE – Your 2025 Guide
          </h1>
          <p className="text-xl mb-8 opacity-90">
            Searching for the best business insurance UAE, comparing quotes for the cheapest business insurance Dubai, or just trying to stay compliant?
          </p>
          <Button
            size="lg"
            onClick={() => navigate(tLink('/decision-tree'))}
            className="font-semibold text-lg px-8 py-4"
            style={{
              backgroundColor: tenant?.colorScheme?.startMatrixButtonColor || undefined,
              color: tenant?.colorScheme?.startMatrixButtonColor ? '#FFFFFF' : undefined
            }}
          >
            Get Your Quote Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        
        {/* What's Mandatory */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            1 | What's 100% Mandatory in the UAE?
          </h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-red-500 pl-6 py-4 bg-red-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                Employee Medical Insurance
              </h3>
              <p className="text-gray-700 mb-2">
                <strong>Who Must Buy:</strong> Any employer sponsoring visas (mainland + free zone)
              </p>
              <p className="text-gray-700">
                <strong>Legal Trigger:</strong> DHA (Dubai) / DOH (Abu Dhabi) block visa issuance without proof
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-6 py-4 bg-red-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                Workmen's Compensation
              </h3>
              <p className="text-gray-700 mb-2">
                <strong>Who Must Buy:</strong> Firms with manual or site staff – construction, logistics, facilities
              </p>
              <p className="text-gray-700">
                <strong>Legal Trigger:</strong> Federal Decree-Law 33/2021: employer liable for workplace injury
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-6 py-4 bg-red-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                Motor Third-Party Liability
              </h3>
              <p className="text-gray-700 mb-2">
                <strong>Who Must Buy:</strong> All company-owned vehicles
              </p>
              <p className="text-gray-700">
                <strong>Legal Trigger:</strong> UAE Traffic Law – no insurance, no registration
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-6 py-4 bg-red-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                Professional Indemnity Insurance
              </h3>
              <p className="text-gray-700 mb-2">
                <strong>Who Must Buy:</strong> Regulated advisers & DIFC/DFSA Category 3B-4 firms
              </p>
              <p className="text-gray-700">
                <strong>Legal Trigger:</strong> DFSA PIB 6.12 requires adequate PII
              </p>
            </div>
          </div>
        </section>

        {/* Optional Cover */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            2 | Best-Value Optional Cover Most SMEs Pick
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border-l-4 border-green-500 pl-6 py-4 bg-green-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                Public Liability Insurance Dubai
              </h3>
              <p className="text-gray-700 mb-2">Pays for injuries or property damage to visitors, clients, neighbours</p>
              <p className="font-semibold text-green-700">AED 1m – 10m</p>
            </div>

            <div className="border-l-4 border-green-500 pl-6 py-4 bg-green-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                Property All-Risk Insurance UAE
              </h3>
              <p className="text-gray-700 mb-2">Covers office fit-out, stock, laptops against fire, flood, theft</p>
              <p className="font-semibold text-green-700">AED 250k – 20m</p>
            </div>

            <div className="border-l-4 border-green-500 pl-6 py-4 bg-green-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                Cyber Insurance UAE
              </h3>
              <p className="text-gray-700 mb-2">Ransomware & data-breach fines (yes, in the GCC too)</p>
              <p className="font-semibold text-green-700">AED 500k – 5m</p>
            </div>

            <div className="border-l-4 border-green-500 pl-6 py-4 bg-green-50">
              <h3 className="font-bold text-lg mb-2 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                Business Interruption Insurance
              </h3>
              <p className="text-gray-700 mb-2">Keeps wages & rent flowing after disaster</p>
              <p className="font-semibold text-green-700">6-12 months gross profit</p>
            </div>
          </div>
        </section>

        {/* Money-Saving Tips */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            3 | How to Get the Cheapest Business Insurance Without Cutting Cover
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">1</div>
              <p className="text-gray-700">Bundle policies. Insurers give 5-15% discounts when you package medical, liability and property.</p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">2</div>
              <p className="text-gray-700">Raise deductibles. A higher excess trims premiums—just keep cash reserves handy.</p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">3</div>
              <p className="text-gray-700">Compare business insurance UAE online. Aggregators like YallaCompare for Business surface real rates in minutes.</p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">4</div>
              <p className="text-gray-700">Pay annually. Monthly instalments cost ~4% extra.</p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">5</div>
              <p className="text-gray-700">Renew early. Underwriters penalise last-minute buyers; quote 30 days out for "early-bird" pricing.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-12 bg-gray-50 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Get the Best, Cheapest Business Insurance Quotes Now
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Skip cold-calling brokers. YallaCompare for Business scans leading UAE insurers, finds the best business insurance deals, and flags every mandatory policy so you stay legal.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate(tLink('/decision-tree'))}
              className="font-semibold text-lg px-8 py-4"
              style={{
                backgroundColor: tenant?.colorScheme?.startMatrixButtonColor || undefined,
                color: tenant?.colorScheme?.startMatrixButtonColor ? '#FFFFFF' : undefined
              }}
            >
              Get Instant Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate(tLink('/'))}
              className="font-semibold text-lg px-8 py-4"
            >
              View All Products
            </Button>
          </div>
        </section>

      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm opacity-70">
            Powered by YallaCompare Insurance Broker LLC #20
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BusinessInsuranceGuide;
